import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useInviteStore from "../stores/inviteStore";
import useNSFWStore from "../stores/nsfwStore";

type OnboardingStep = "nickname" | "source";

type OnboardingData = {
  nickname?: string;
  source?: string;
  invite_code?: string;
};

const SOURCE_OPTIONS = [
  "friends",
  "tiktok",
  "instagram",
  "youtube",
  "reddit",
  "twitter",
  "app_stores",
  "llm",
  "google_search",
  "bing_search",
  "online_articles",
  "paid_ad",
  "other",
  "dont_remember",
] as const;

const fieldLabelSx = {
  mb: 1,
  fontWeight: 700,
  color: "common.white",
} as const;

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (data: OnboardingData) => void;
}

export function OnboardingModal({
  open,
  onClose,
  onComplete,
}: OnboardingModalProps) {
  const { t } = useTranslation();
  const sourceDescription = t("onboarding.sourceDescription");
  const [step, setStep] = useState<OnboardingStep>("nickname");
  const [nickname, setNickname] = useState("");
  const [source, setSource] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [inviteCodeError, setInviteCodeError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get invite code from store
  const inviteCodeFromStore = useInviteStore((state) => state.inviteCode);

  // NSFW store settings
  const isNSFWEnabled = useNSFWStore((state) => state.isNSFWEnabled);
  const isAgeVerified = useNSFWStore((state) => state.isAgeVerified);
  const setNSFWEnabled = useNSFWStore((state) => state.setNSFWEnabled);
  const setAgeVerified = useNSFWStore((state) => state.setAgeVerified);

  useEffect(() => {
    if (open) {
      setStep("nickname");
      setGeneralError(null);
      setNicknameError(null);
      setInviteCodeError(null);
    }
  }, [open]);

  // Auto-populate invite code when modal opens and store has a code
  useEffect(() => {
    if (open && inviteCodeFromStore && inviteCode.trim() === "") {
      setInviteCode(inviteCodeFromStore);
    }
  }, [open, inviteCodeFromStore, inviteCode]);

  const validateNickname = (value: string) => {
    if (value && value.length > 20) {
      setNicknameError(t("onboarding.nicknameError"));
      return false;
    }
    setNicknameError(null);
    return true;
  };

  const validateInviteCode = (value: string) => {
    if (value && (value.length > 10 || !/^[a-zA-Z0-9]*$/.test(value))) {
      setInviteCodeError(t("onboarding.inviteCodeError"));
      return false;
    }
    setInviteCodeError(null);
    return true;
  };

  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNickname(value);
    validateNickname(value);
  };

  const handleInviteCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInviteCode(value);
    validateInviteCode(value);
  };

  const handleNext = async () => {
    // Clear previous errors
    setGeneralError(null);

    const isNicknameValid = validateNickname(nickname);

    if (!isNicknameValid) {
      return;
    }

    if (step === "nickname") {
      setStep("source");
      return;
    }

    const isInviteCodeValid = validateInviteCode(inviteCode);
    if (!isInviteCodeValid) {
      return;
    }

    setIsLoading(true);

    try {
      const data: OnboardingData = {};

      if (nickname.trim()) {
        data.nickname = nickname.trim();
      }

      if (source) {
        data.source = source;
      }

      if (inviteCode.trim()) {
        data.invite_code = inviteCode.trim().toLowerCase();
      }

      await onComplete(data);
    } catch (error) {
      console.error("Onboarding error:", error);

      // Display error message to user
      if (error instanceof Error) {
        setGeneralError(error.message);
      } else {
        setGeneralError(t("onboarding.error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    // Clear previous errors
    setGeneralError(null);

    setIsLoading(true);
    try {
      await onComplete({});
    } catch (error) {
      console.error("Onboarding skip error:", error);

      // Display error message to user
      if (error instanceof Error) {
        setGeneralError(error.message);
      } else {
        setGeneralError(t("onboarding.error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <ModalDialog
        sx={{
          width: { xs: "100vw", sm: "min(90vw, 400px)" },
          maxWidth: { xs: "100vw", sm: 400 },
          height: { xs: "100dvh", sm: "auto" },
          maxHeight: { xs: "100dvh", sm: "90vh" },
          m: { xs: 0, sm: 2 },
          px: { xs: 2.5, sm: 3 },
          pt: {
            xs: "max(12px, env(safe-area-inset-top))",
            sm: 3,
          },
          pb: {
            xs: "max(12px, env(safe-area-inset-bottom))",
            sm: 3,
          },
          borderRadius: { xs: 0, sm: "lg" },
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography level="title-lg" sx={{ flex: 1, fontSize: "1.1rem" }}>
            {t("onboarding.title")}
          </Typography>
          <IconButton
            onClick={handleSkip}
            size="sm"
            sx={{ p: 0.5 }}
            disabled={isLoading}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "center",
            gap: 0.75,
          }}
        >
          {[0, 1].map((idx) => {
            const isActive =
              (step === "nickname" && idx === 0) ||
              (step === "source" && idx === 1);
            return (
              <Box
                key={idx}
                sx={{
                  width: isActive ? 18 : 8,
                  height: 8,
                  borderRadius: 999,
                  transition: "all 0.2s ease",
                  bgcolor: isActive ? "common.white" : "neutral.500",
                  opacity: isActive ? 1 : 0.45,
                }}
              />
            );
          })}
        </Box>

        <Stack spacing={2}>
          {step === "nickname" ? (
            <div>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}
              >
                {t("onboarding.accountSettings")}
              </Typography>
              <Typography level="body-sm" sx={fieldLabelSx}>
                {t("onboarding.nickname")}
              </Typography>
              <Input
                placeholder={t("onboarding.nicknamePlaceholder")}
                value={nickname}
                onChange={handleNicknameChange}
                error={Boolean(nicknameError)}
                disabled={isLoading}
                sx={{ width: "100%" }}
              />
              {nicknameError && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                  {nicknameError}
                </Typography>
              )}
              <Typography
                level="body-xs"
                sx={{ mt: 0.5, color: "text.tertiary" }}
              >
                {t("onboarding.nicknameDescription")}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}
              >
                {t("onboarding.deviceSettings")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography level="body-sm" sx={fieldLabelSx}>
                    {t("onboarding.nsfwEnabled")}
                  </Typography>
                  <Typography
                    level="body-xs"
                    sx={{ mt: 0.5, color: "text.tertiary" }}
                  >
                    {t("onboarding.nsfwEnabledDescription")}
                  </Typography>
                </Box>
                <Switch
                  checked={isNSFWEnabled}
                  onChange={(e) => {
                    if (!isAgeVerified && e.target.checked) {
                      setAgeVerified(true);
                    }
                    setNSFWEnabled(e.target.checked);
                  }}
                  color={isNSFWEnabled ? "danger" : "neutral"}
                  disabled={isLoading}
                  sx={{ ml: 2 }}
                />
              </Box>
            </div>
          ) : (
            <>
              <div>
                <Typography level="body-sm" sx={fieldLabelSx}>
                  {t("onboarding.source")}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                  }}
                >
                  {SOURCE_OPTIONS.map((option) => (
                    <Button
                      key={option}
                      size="sm"
                      variant={source === option ? "solid" : "soft"}
                      color={source === option ? "primary" : "neutral"}
                      disabled={isLoading}
                      onClick={() => setSource(option)}
                      sx={{ borderRadius: "999px", fontWeight: 400 }}
                    >
                      {t(`onboarding.sourceOptions.${option}`)}
                    </Button>
                  ))}
                </Box>
                {sourceDescription && (
                  <Typography
                    level="body-xs"
                    sx={{ mt: 0.5, color: "text.tertiary" }}
                  >
                    {sourceDescription}
                  </Typography>
                )}
              </div>

              <div>
                <Typography level="body-sm" sx={fieldLabelSx}>
                  {t("onboarding.inviteCode")}
                </Typography>
                <Input
                  placeholder={t("onboarding.inviteCodePlaceholder")}
                  value={inviteCode}
                  onChange={handleInviteCodeChange}
                  error={Boolean(inviteCodeError)}
                  disabled={isLoading}
                  sx={{ width: "100%" }}
                />
                {inviteCodeError && (
                  <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                    {inviteCodeError}
                  </Typography>
                )}
                <Typography
                  level="body-xs"
                  sx={{ mt: 0.5, color: "text.tertiary" }}
                >
                  {t("onboarding.inviteCodeDescription")}
                </Typography>
              </div>
            </>
          )}
        </Stack>

        {generalError && (
          <Typography
            level="body-sm"
            color="danger"
            sx={{ mt: 2, textAlign: "center" }}
          >
            {generalError}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 2 }}
        >
          {step === "source" && (
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setStep("nickname")}
              disabled={isLoading}
            >
              {t("onboarding.back")}
            </Button>
          )}
          <Button onClick={handleNext} loading={isLoading} disabled={isLoading}>
            {step === "nickname"
              ? t("onboarding.nextStep")
              : t("onboarding.next")}
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
